import { forwardRef } from 'react';
import { IMuxVideoUploaderMuxAssetEntityResponse } from '@/graphql/__generated__/global/types';
import classNames from '@/lib/classNames';
import { VariantProps, cva } from 'class-variance-authority';
import { ResponsiveImage, ResponsiveImageProps } from '../ResponsiveImage';
import { ResponsiveVideo, ResponsiveVideoProps } from '../ResponsiveVideo';

/**
 * Set our default background colours
 */
const cardVariants = cva('relative rounded-3xl overflow-clip isolate', {
    variants: {
        background: {
            none: '',
            sand: 'bg-sand',
            shell: 'bg-shell',
        },
    },
    defaultVariants: {
        background: 'none',
    },
});

export interface CardProps extends React.HTMLAttributes<HTMLDivElement>, VariantProps<typeof cardVariants> {
    /**
     * Should we change this to just be an image component?
     */
    image?: string;
    deferHoverImageLoad?: boolean;
    hoverImage?: string;
    alt?: string;
    sizes?: string;
    aspectRatio?: number;
    video?: IMuxVideoUploaderMuxAssetEntityResponse;
    tintImage?: boolean;
}

const Card = forwardRef<HTMLDivElement, CardProps>(
    ({ className, background, image, hoverImage, deferHoverImageLoad, video, children, sizes, aspectRatio, tintImage = true, ...props }, ref) => (
        <div
            ref={ref}
            key={props.alt}
            className={classNames(cardVariants({ background, className }), image || video?.data ? 'text-white flex items-center' : 'text-primary', hoverImage && 'group')}
            {...props}
        >
            {!video?.data && image && (
                <>
                    <CardImage
                        src={image}
                        aspectRatio={aspectRatio}
                        sizes={sizes}
                        alt={props.alt ?? ''}
                        className="rounded-3xl transition duration-500 ease-out group-hover:scale-105"
                        loading="lazy"
                        tint={tintImage}
                    />
                    {hoverImage && (
                        <CardImage
                            src={hoverImage}
                            aspectRatio={aspectRatio}
                            sizes={sizes}
                            alt={props.alt ?? ''}
                            className={classNames(
                                'hidden rounded-3xl absolute top-0 left-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500 ease-in-out',
                                deferHoverImageLoad ? 'group-hover:md:block' : ' md:block'
                            )}
                            loading="lazy"
                            quality={75}
                            tint={tintImage}
                        />
                    )}
                </>
            )}
            {video?.data && <CardVideo video={video} aspectRatio={aspectRatio} className="rounded-3xl" />}
            {children}
        </div>
    )
);
Card.displayName = 'Card';

const CardImage = forwardRef<HTMLImageElement, ResponsiveImageProps & { tint: boolean }>(({ tint, ...props }, ref) => {
    return (
        <>
            <ResponsiveImage ref={ref} addOverlay={tint} {...props} />
            {tint && <div className="absolute inset-0 bg-black/25 z-10" />}
        </>
    );
});
CardImage.displayName = 'CardImage';

const CardVideo = forwardRef<HTMLVideoElement, ResponsiveVideoProps>((props, ref) => <ResponsiveVideo ref={ref} {...props} />);
CardVideo.displayName = 'CardVideo';

const cardContentVariants = cva('flex flex-col items-start justify-end h-full p-6', {
    variants: {
        align: {
            bottom: 'justify-end',
            center: 'justify-center items-center text-center',
        },
        spacing: {
            tight: '',
            center: 'justify-center',
            between: 'justify-between',
        },
    },
    defaultVariants: {
        align: 'bottom',
        spacing: 'tight',
    },
});

export interface CardContentProps extends React.HTMLAttributes<HTMLDivElement>, VariantProps<typeof cardContentVariants> {}

const CardContent = forwardRef<HTMLDivElement, CardContentProps>(({ align, spacing, className, ...props }, ref) => (
    <div ref={ref} className={classNames(cardContentVariants({ align, spacing, className }))} {...props} />
));
CardContent.displayName = 'CardContent';

const CardEyebrow = forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(({ className, ...props }, ref) => <div ref={ref} className={classNames('h6', className)} {...props} />);
CardEyebrow.displayName = 'CardEyebrow';

const CardTitle = forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement> & { as?: 'h3' | 'h4' }>(({ className, as = 'h3', ...props }, ref) => {
    if (as === 'h4') {
        return <h4 ref={ref} className={classNames('h4 mb-4', className)} {...props} />;
    }

    return <h3 ref={ref} className={classNames('h3 mb-4', className)} {...props} />;
});
CardTitle.displayName = 'CardTitle';

const CardDescription = forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(({ className, ...props }, ref) => (
    <div ref={ref} className={classNames('p text-primary', className)} {...props} />
));
CardDescription.displayName = 'CardDescription';

export { Card, CardContent, CardDescription, CardEyebrow, CardImage, CardTitle };
